import { createFileRoute, Link, Outlet, useChildMatches } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, FolderKanban } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useActiveCompany } from "@/hooks/use-active-company";
import { ProjectModal } from "@/components/projects/ProjectModal";
import { projectsLabel, projectSingularLabel } from "@/modules/navigation";

export const Route = createFileRoute("/_authenticated/projects")({
  component: ProjectsIndex,
  head: () => ({
    meta: [
      { title: "Prosjekter | VERQO" },
      { name: "description", content: "Alle prosjekter i VERQO — samlet fra befaring til tilbud." },
    ],
  }),
});

function ProjectsIndex() {
  // Denne ruten er forelder til projects.$id. Når detaljruten er aktiv må vi
  // rendre <Outlet /> så prosjektet vises; ellers ville detaljsiden aldri
  // blitt synlig fra lista/dashboardet. Uten aktiv underrute viser vi lista.
  // (Dypere ruter – documentation/visit – er nøstet under projects.$id og
  // trenger tilsvarende Outlet der; tas som egen ruteopprydding.)
  const childMatches = useChildMatches();
  const { activeModuleKey: moduleKey } = useActiveCompany();
  const [creating, setCreating] = useState(false);
  const listLabel = projectsLabel(moduleKey);
  const singular = projectSingularLabel(moduleKey);
  const { data, isLoading } = useQuery({
    queryKey: ["projects-list", moduleKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number, status, updated_at, module_key")
        .eq("module_key", moduleKey)
        .order("updated_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (childMatches.length > 0) return <Outlet />;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 lg:p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
            <FolderKanban className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{listLabel}</h1>
            <p className="text-sm text-muted-foreground">Åpne et {singular} for å jobbe videre.</p>
          </div>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="mr-1 h-4 w-4" /> Nytt {singular}
        </Button>
      </header>

      <Card className="p-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Laster prosjekter…</div>
        ) : (data ?? []).length === 0 ? (
          <div className="flex flex-col items-start gap-3 text-sm text-muted-foreground">
            <p>Ingen {listLabel.toLowerCase()} ennå. Opprett ditt første {singular} for å komme i gang.</p>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1 h-4 w-4" /> Nytt {singular}
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data!.map((p) => (
              <li key={p.id}>
                <Link
                  to="/projects/$id"
                  params={{ id: p.id }}
                  className="flex items-center justify-between gap-3 py-3 hover:bg-accent/40 -mx-2 px-2 rounded"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.project_number ?? "—"} · {p.status ?? "aktivt"} · {p.module_key ?? "—"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">Åpne</Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {creating && <ProjectModal onClose={() => setCreating(false)} />}
    </div>
  );
}
